/**
 * NativeService -- Unified native capability wrapper with web fallbacks.
 *
 * Every method gracefully degrades on web browsers:
 * - Push notifications -> Web Push API
 * - Local notifications -> Notification API
 * - Biometrics -> returns false (not available on web)
 * - Haptics -> navigator.vibrate()
 * - Badge -> navigator.setAppBadge()
 * - Camera -> input[type=file]
 * - Share -> navigator.share() or clipboard
 * - etc.
 *
 * Usage:
 *   import { NativeService } from './NativeService';
 *   if (NativeService.isNative()) { ... }
 *   await NativeService.vibrate('light');
 */

import { Capacitor } from '@capacitor/core';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScheduleNotificationOpts {
  title: string;
  body: string;
  at: Date;
  id: number;
}

export interface ShareOpts {
  title?: string;
  text?: string;
  url?: string;
}

export interface DeviceInfo {
  platform: string;
  model: string;
  os: string;
  osVersion: string;
  isVirtual: boolean;
}

export interface NetworkStatus {
  connected: boolean;
  type: string;
}

// ---------------------------------------------------------------------------
// Lazy plugin imports -- only loaded when actually called on native
// ---------------------------------------------------------------------------

async function getAppPlugin() {
  const { App } = await import('@capacitor/app');
  return App;
}

async function getHapticsPlugin() {
  const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
  return { Haptics, ImpactStyle };
}

async function getStatusBarPlugin() {
  const { StatusBar, Style } = await import('@capacitor/status-bar');
  return { StatusBar, Style };
}

async function getKeyboardPlugin() {
  const { Keyboard } = await import('@capacitor/keyboard');
  return Keyboard;
}

async function getCameraPlugin() {
  const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
  return { Camera, CameraResultType, CameraSource };
}

async function getFilesystemPlugin() {
  const { Filesystem, Directory, Encoding } = await import('@capacitor/filesystem');
  return { Filesystem, Directory, Encoding };
}

async function getSharePlugin() {
  const { Share } = await import('@capacitor/share');
  return Share;
}

async function getClipboardPlugin() {
  const { Clipboard } = await import('@capacitor/clipboard');
  return Clipboard;
}

async function getNetworkPlugin() {
  const { Network } = await import('@capacitor/network');
  return Network;
}

async function getDevicePlugin() {
  const { Device } = await import('@capacitor/device');
  return Device;
}

async function getToastPlugin() {
  const { Toast } = await import('@capacitor/toast');
  return Toast;
}

async function getDialogPlugin() {
  const { Dialog } = await import('@capacitor/dialog');
  return Dialog;
}

async function getPushPlugin() {
  const { PushNotifications } = await import('@capacitor/push-notifications');
  return PushNotifications;
}

async function getLocalNotifPlugin() {
  const { LocalNotifications } = await import('@capacitor/local-notifications');
  return LocalNotifications;
}

// ---------------------------------------------------------------------------
// NativeService
// ---------------------------------------------------------------------------

export class NativeService {
  // ── Platform detection ──────────────────────────────────────

  static isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  static getPlatform(): 'web' | 'ios' | 'android' {
    return Capacitor.getPlatform() as 'web' | 'ios' | 'android';
  }

  // ── Push Notifications ──────────────────────────────────────

  static async requestPushPermission(): Promise<boolean> {
    if (NativeService.isNative()) {
      try {
        const Push = await getPushPlugin();
        const result = await Push.requestPermissions();
        return result.receive === 'granted';
      } catch {
        return false;
      }
    }

    // Web fallback: Notification API
    if (!('Notification' in window)) return false;
    const result = await Notification.requestPermission();
    return result === 'granted';
  }

  static async registerPush(): Promise<string | null> {
    if (NativeService.isNative()) {
      try {
        const Push = await getPushPlugin();
        const permission = await Push.requestPermissions();
        if (permission.receive !== 'granted') return null;

        await Push.register();

        return new Promise((resolve) => {
          Push.addListener('registration', (token) => {
            resolve(token.value);
          });
          // Timeout after 10s
          setTimeout(() => resolve(null), 10_000);
        });
      } catch {
        return null;
      }
    }

    // Web fallback: try Web Push via service worker
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
      const registration = await navigator.serviceWorker.ready;
      const vapidKey = NativeService._getVAPIDKey();
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        ...(vapidKey ? { applicationServerKey: vapidKey.buffer as ArrayBuffer } : {}),
      });
      return JSON.stringify(subscription.toJSON());
    } catch {
      return null;
    }
  }

  private static _getVAPIDKey(): Uint8Array | undefined {
    // VAPID key should be set in env/config; return undefined if not available
    const key = (window as unknown as Record<string, unknown>).__AWENI_VAPID_KEY as string | undefined;
    if (!key) return undefined;
    const padding = '='.repeat((4 - (key.length % 4)) % 4);
    const base64 = (key + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
  }

  // ── Local Notifications ─────────────────────────────────────

  static async scheduleLocalNotification(opts: ScheduleNotificationOpts): Promise<void> {
    if (NativeService.isNative()) {
      try {
        const LocalNotif = await getLocalNotifPlugin();
        const perm = await LocalNotif.requestPermissions();
        if (perm.display !== 'granted') return;

        await LocalNotif.schedule({
          notifications: [
            {
              title: opts.title,
              body: opts.body,
              id: opts.id,
              schedule: { at: opts.at },
              sound: 'default',
              smallIcon: 'ic_stat_notify',
              iconColor: '#0A0A0A',
            },
          ],
        });
        return;
      } catch {
        // Fall through to web
      }
    }

    // Web fallback: use setTimeout + Notification API
    const delay = opts.at.getTime() - Date.now();
    if (delay <= 0) return;

    if ('Notification' in window && Notification.permission === 'granted') {
      setTimeout(() => {
        try {
          new Notification(opts.title, {
            body: opts.body,
            icon: '/icons/icon-192x192.png',
            badge: '/icons/icon-72x72.png',
            tag: `aweni-local-${opts.id}`,
          });
        } catch { /* Notification not available in this context */ }
      }, delay);
    }
  }

  static async cancelLocalNotification(id: number): Promise<void> {
    if (NativeService.isNative()) {
      try {
        const LocalNotif = await getLocalNotifPlugin();
        await LocalNotif.cancel({ notifications: [{ id }] });
      } catch { /* silent */ }
    }
    // Web: no reliable way to cancel setTimeout-based notifications
  }

  // ── Biometric Auth ──────────────────────────────────────────

  static async hasBiometric(): Promise<boolean> {
    // Biometric requires a community plugin (e.g. capacitor-native-biometric)
    // For now, return false on all platforms — will be enabled when plugin is added
    return false;
  }

  static async authenticateBiometric(_reason: string): Promise<boolean> {
    // Placeholder — biometric plugin not yet installed
    return false;
  }

  // ── Haptics ─────────────────────────────────────────────────

  static async vibrate(style: 'light' | 'medium' | 'heavy' = 'light'): Promise<void> {
    if (NativeService.isNative()) {
      try {
        const { Haptics, ImpactStyle } = await getHapticsPlugin();
        const map: Record<string, unknown> = {
          light: ImpactStyle.Light,
          medium: ImpactStyle.Medium,
          heavy: ImpactStyle.Heavy,
        };
        await Haptics.impact({ style: map[style] as never });
        return;
      } catch { /* fall through */ }
    }

    // Web fallback: navigator.vibrate
    if ('vibrate' in navigator) {
      const durations: Record<string, number> = { light: 10, medium: 30, heavy: 50 };
      navigator.vibrate(durations[style] || 10);
    }
  }

  // ── Badge ───────────────────────────────────────────────────

  static async setBadge(count: number): Promise<void> {
    // Native: badge count is set via push notification payload on iOS;
    // on Android, use launcher shortcuts. No official @capacitor/badge plugin.
    // Use the Web Badge API which works on supported browsers and installed PWAs.
    try {
      if ('setAppBadge' in navigator) {
        await (navigator as unknown as { setAppBadge: (n: number) => Promise<void> }).setAppBadge(count);
      }
    } catch { /* silent */ }
  }

  static async clearBadge(): Promise<void> {
    try {
      if ('clearAppBadge' in navigator) {
        await (navigator as unknown as { clearAppBadge: () => Promise<void> }).clearAppBadge();
      }
    } catch { /* silent */ }
  }

  // ── Camera ──────────────────────────────────────────────────

  static async takePhoto(): Promise<string> {
    if (NativeService.isNative()) {
      try {
        const { Camera, CameraResultType, CameraSource } = await getCameraPlugin();
        const photo = await Camera.getPhoto({
          quality: 80,
          allowEditing: false,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Camera,
          width: 1200,
        });
        return photo.dataUrl || '';
      } catch {
        return '';
      }
    }

    // Web fallback: file input with capture
    return NativeService._pickFileAsDataUrl('image/*', true);
  }

  static async pickPhoto(): Promise<string> {
    if (NativeService.isNative()) {
      try {
        const { Camera, CameraResultType, CameraSource } = await getCameraPlugin();
        const photo = await Camera.getPhoto({
          quality: 80,
          allowEditing: false,
          resultType: CameraResultType.DataUrl,
          source: CameraSource.Photos,
          width: 1200,
        });
        return photo.dataUrl || '';
      } catch {
        return '';
      }
    }

    // Web fallback: file input
    return NativeService._pickFileAsDataUrl('image/*', false);
  }

  private static _pickFileAsDataUrl(accept: string, capture: boolean): Promise<string> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      if (capture) input.setAttribute('capture', 'environment');
      input.style.display = 'none';
      document.body.appendChild(input);

      input.addEventListener('change', () => {
        const file = input.files?.[0];
        if (!file) {
          resolve('');
          input.remove();
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result as string);
          input.remove();
        };
        reader.onerror = () => {
          resolve('');
          input.remove();
        };
        reader.readAsDataURL(file);
      });

      input.addEventListener('cancel', () => {
        resolve('');
        input.remove();
      });

      input.click();
    });
  }

  // ── Share ───────────────────────────────────────────────────

  static async share(opts: ShareOpts): Promise<void> {
    if (NativeService.isNative()) {
      try {
        const SharePlugin = await getSharePlugin();
        await SharePlugin.share({
          title: opts.title,
          text: opts.text,
          url: opts.url,
          dialogTitle: opts.title,
        });
        return;
      } catch { /* fall through */ }
    }

    // Web fallback: navigator.share or clipboard
    if ('share' in navigator) {
      try {
        await navigator.share({
          title: opts.title,
          text: opts.text,
          url: opts.url,
        });
        return;
      } catch { /* user cancelled or not supported */ }
    }

    // Last resort: copy URL to clipboard
    const text = opts.url || opts.text || '';
    await NativeService.copyToClipboard(text);
  }

  // ── Clipboard ───────────────────────────────────────────────

  static async copyToClipboard(text: string): Promise<void> {
    if (NativeService.isNative()) {
      try {
        const ClipboardPlugin = await getClipboardPlugin();
        await ClipboardPlugin.write({ string: text });
        return;
      } catch { /* fall through */ }
    }

    // Web fallback
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Older fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px;';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
  }

  // ── Network ─────────────────────────────────────────────────

  static async getNetworkStatus(): Promise<NetworkStatus> {
    if (NativeService.isNative()) {
      try {
        const NetworkPlugin = await getNetworkPlugin();
        const status = await NetworkPlugin.getStatus();
        return { connected: status.connected, type: status.connectionType };
      } catch { /* fall through */ }
    }

    // Web fallback
    return {
      connected: navigator.onLine,
      type: navigator.onLine ? 'unknown' : 'none',
    };
  }

  static onNetworkChange(callback: (connected: boolean) => void): () => void {
    if (NativeService.isNative()) {
      let listenerHandle: { remove: () => void } | null = null;

      getNetworkPlugin().then((NetworkPlugin) => {
        NetworkPlugin.addListener('networkStatusChange', (status) => {
          callback(status.connected);
        }).then((handle) => {
          listenerHandle = handle;
        });
      }).catch(() => { /* silent */ });

      return () => {
        listenerHandle?.remove();
      };
    }

    // Web fallback
    const onlineHandler = () => callback(true);
    const offlineHandler = () => callback(false);
    window.addEventListener('online', onlineHandler);
    window.addEventListener('offline', offlineHandler);
    return () => {
      window.removeEventListener('online', onlineHandler);
      window.removeEventListener('offline', offlineHandler);
    };
  }

  // ── Device Info ─────────────────────────────────────────────

  static async getDeviceInfo(): Promise<DeviceInfo> {
    if (NativeService.isNative()) {
      try {
        const DevicePlugin = await getDevicePlugin();
        const info = await DevicePlugin.getInfo();
        return {
          platform: info.platform,
          model: info.model,
          os: info.operatingSystem,
          osVersion: info.osVersion,
          isVirtual: info.isVirtual,
        };
      } catch { /* fall through */ }
    }

    // Web fallback
    const ua = navigator.userAgent;
    return {
      platform: 'web',
      model: ua.includes('Mobile') ? 'mobile' : 'desktop',
      os: navigator.platform || 'unknown',
      osVersion: '',
      isVirtual: false,
    };
  }

  // ── Status Bar ──────────────────────────────────────────────

  static async setStatusBarStyle(style: 'light' | 'dark'): Promise<void> {
    if (!NativeService.isNative()) return;
    try {
      const { StatusBar, Style } = await getStatusBarPlugin();
      await StatusBar.setStyle({
        style: style === 'dark' ? Style.Dark : Style.Light,
      });
    } catch { /* silent */ }
  }

  static async setStatusBarColor(color: string): Promise<void> {
    if (!NativeService.isNative()) return;
    if (NativeService.getPlatform() !== 'android') return;
    try {
      const { StatusBar } = await getStatusBarPlugin();
      await StatusBar.setBackgroundColor({ color });
    } catch { /* silent */ }
  }

  static async hideStatusBar(): Promise<void> {
    if (!NativeService.isNative()) return;
    try {
      const { StatusBar } = await getStatusBarPlugin();
      await StatusBar.hide();
    } catch { /* silent */ }
  }

  static async showStatusBar(): Promise<void> {
    if (!NativeService.isNative()) return;
    try {
      const { StatusBar } = await getStatusBarPlugin();
      await StatusBar.show();
    } catch { /* silent */ }
  }

  // ── Keyboard ────────────────────────────────────────────────

  static onKeyboardShow(callback: (height: number) => void): () => void {
    if (NativeService.isNative()) {
      let handle: { remove: () => void } | null = null;
      getKeyboardPlugin().then((Keyboard) => {
        Keyboard.addListener('keyboardWillShow', (info) => {
          callback(info.keyboardHeight);
        }).then((h) => { handle = h; });
      }).catch(() => { /* silent */ });

      return () => { handle?.remove(); };
    }

    // Web fallback: use visualViewport resize
    const handler = () => {
      if (window.visualViewport) {
        const heightDiff = window.innerHeight - window.visualViewport.height;
        if (heightDiff > 100) {
          callback(heightDiff);
        }
      }
    };
    window.visualViewport?.addEventListener('resize', handler);
    return () => {
      window.visualViewport?.removeEventListener('resize', handler);
    };
  }

  static onKeyboardHide(callback: () => void): () => void {
    if (NativeService.isNative()) {
      let handle: { remove: () => void } | null = null;
      getKeyboardPlugin().then((Keyboard) => {
        Keyboard.addListener('keyboardWillHide', () => {
          callback();
        }).then((h) => { handle = h; });
      }).catch(() => { /* silent */ });

      return () => { handle?.remove(); };
    }

    // Web fallback
    const handler = () => {
      if (window.visualViewport) {
        const heightDiff = window.innerHeight - window.visualViewport.height;
        if (heightDiff < 100) {
          callback();
        }
      }
    };
    window.visualViewport?.addEventListener('resize', handler);
    return () => {
      window.visualViewport?.removeEventListener('resize', handler);
    };
  }

  // ── App Lifecycle ───────────────────────────────────────────

  static onAppStateChange(callback: (isActive: boolean) => void): () => void {
    if (NativeService.isNative()) {
      let handle: { remove: () => void } | null = null;
      getAppPlugin().then((AppPlugin) => {
        AppPlugin.addListener('appStateChange', (state) => {
          callback(state.isActive);
        }).then((h) => { handle = h; });
      }).catch(() => { /* silent */ });

      return () => { handle?.remove(); };
    }

    // Web fallback: visibilitychange
    const handler = () => {
      callback(document.visibilityState === 'visible');
    };
    document.addEventListener('visibilitychange', handler);
    return () => {
      document.removeEventListener('visibilitychange', handler);
    };
  }

  static onBackButton(callback: () => void): () => void {
    if (NativeService.isNative()) {
      let handle: { remove: () => void } | null = null;
      getAppPlugin().then((AppPlugin) => {
        AppPlugin.addListener('backButton', () => {
          callback();
        }).then((h) => { handle = h; });
      }).catch(() => { /* silent */ });

      return () => { handle?.remove(); };
    }

    // No web fallback. Wiring `popstate` to a callback that runs
    // `history.back()` (as main.ts does for the Android back button)
    // creates an infinite loop on web: popstate fires → handler runs
    // history.back() → history.back() fires popstate → handler runs
    // history.back() again → eventually navigates OUT of /aweni/ to the
    // browser's previous origin (about:privatebrowsing in private windows,
    // about:newtab in normal windows). Confirmed observation 2026-04-30.
    // The browser already handles its own back button natively on web —
    // we don't need to (and must not) intercept it.
    return () => { /* no-op */ };
  }

  // ── Toast ───────────────────────────────────────────────────

  static async showToast(
    message: string,
    duration: 'short' | 'long' = 'short',
  ): Promise<void> {
    if (NativeService.isNative()) {
      try {
        const ToastPlugin = await getToastPlugin();
        await ToastPlugin.show({ text: message, duration });
        return;
      } catch { /* fall through */ }
    }

    // Web fallback: custom toast element
    const existing = document.getElementById('aweni-native-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.id = 'aweni-native-toast';
    toast.textContent = message;
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.style.cssText = `
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0,0,0,0.85);
      color: #fff;
      padding: 10px 20px;
      border-radius: 8px;
      font-size: 14px;
      z-index: 10000;
      pointer-events: none;
      opacity: 0;
      transition: opacity var(--motion-duration-base) var(--motion-easing-default);
      max-width: 80vw;
      text-align: center;
    `;
    document.body.appendChild(toast);

    // Fade in
    requestAnimationFrame(() => { toast.style.opacity = '1'; });

    // Auto-dismiss
    const ms = duration === 'long' ? 3500 : 2000;
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, ms);
  }

  // ── Dialog ──────────────────────────────────────────────────

  static async showAlert(title: string, message: string): Promise<void> {
    if (NativeService.isNative()) {
      try {
        const DialogPlugin = await getDialogPlugin();
        await DialogPlugin.alert({ title, message });
        return;
      } catch { /* fall through */ }
    }

    // Web fallback
    window.alert(`${title}\n\n${message}`);
  }

  static async showConfirm(title: string, message: string): Promise<boolean> {
    if (NativeService.isNative()) {
      try {
        const DialogPlugin = await getDialogPlugin();
        const result = await DialogPlugin.confirm({ title, message });
        return result.value;
      } catch { /* fall through */ }
    }

    return window.confirm(`${title}\n\n${message}`);
  }

  // ── Filesystem ──────────────────────────────────────────────

  static async writeFile(path: string, data: string): Promise<void> {
    if (NativeService.isNative()) {
      try {
        const { Filesystem, Directory, Encoding } = await getFilesystemPlugin();
        await Filesystem.writeFile({
          path,
          data,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
          recursive: true,
        });
        return;
      } catch { /* fall through */ }
    }

    // Web fallback: IndexedDB
    await NativeService._idbWrite(path, data);
  }

  static async readFile(path: string): Promise<string> {
    if (NativeService.isNative()) {
      try {
        const { Filesystem, Directory, Encoding } = await getFilesystemPlugin();
        const result = await Filesystem.readFile({
          path,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        return result.data as string;
      } catch {
        return '';
      }
    }

    // Web fallback: IndexedDB
    return (await NativeService._idbRead(path)) || '';
  }

  static async deleteFile(path: string): Promise<void> {
    if (NativeService.isNative()) {
      try {
        const { Filesystem, Directory } = await getFilesystemPlugin();
        await Filesystem.deleteFile({ path, directory: Directory.Data });
        return;
      } catch { /* fall through */ }
    }

    await NativeService._idbDelete(path);
  }

  static async getStorageInfo(): Promise<{ used: number; total: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage || 0,
        total: estimate.quota || 0,
      };
    }
    return { used: 0, total: 0 };
  }

  // ── IDB helpers for web filesystem fallback ─────────────────

  private static _idbPromise: Promise<IDBDatabase> | null = null;

  private static _openIDB(): Promise<IDBDatabase> {
    if (!NativeService._idbPromise) {
      NativeService._idbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open('aweni_native_fs', 1);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains('files')) {
            db.createObjectStore('files', { keyPath: 'path' });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    }
    return NativeService._idbPromise;
  }

  private static async _idbWrite(path: string, data: string): Promise<void> {
    const db = await NativeService._openIDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('files', 'readwrite');
      tx.objectStore('files').put({ path, data, updatedAt: Date.now() });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private static async _idbRead(path: string): Promise<string | null> {
    const db = await NativeService._openIDB();
    return new Promise((resolve) => {
      const tx = db.transaction('files', 'readonly');
      const req = tx.objectStore('files').get(path);
      req.onsuccess = () => resolve(req.result?.data ?? null);
      req.onerror = () => resolve(null);
    });
  }

  private static async _idbDelete(path: string): Promise<void> {
    const db = await NativeService._openIDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('files', 'readwrite');
      tx.objectStore('files').delete(path);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}
